import SEO from "../components/SEO";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { Button } from "../components/ui/button";
import { 
  Car, 
  Shield, 
  Clock, 
  Users, 
  Star, 
  ChevronRight, 
  Sun, 
  Moon, 
  Menu, 
  X,
  CheckCircle,
  MapPin,
  DollarSign,
  Eye,
  Store
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";
const HERO_BG = "https://images.unsplash.com/photo-1765115570167-76bb5a54f6cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjYXIlMjBvbiUyMG5ldyUyMHplYWxhbmQlMjBzY2VuaWMlMjByb2FkfGVufDB8fHx8MTc3MzUyMjc3OXww&ixlib=rb-4.1.0&q=85";
const MECHANIC_IMG = "https://images.unsplash.com/photo-1565218889312-9bcb403c40c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxtZWNoYW5pYyUyMGNoYW5naW5nJTIwdHlyZSUyMHdvcmtzaG9wfGVufDB8fHx8MTc3MzUyMjc3OXww&ixlib=rb-4.1.0&q=85";
const WORKSHOP_IMG = "https://images.unsplash.com/photo-1675034743126-0f250a5fee51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMGNoYW5naW5nJTIwdHlyZSUyMHdvcmtzaG9wfGVufDB8fHx8MTc3MzUyMjc3OXww&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, userType } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Primary CTA sends users to the guest quote form. Logged-in customers
  // continue to their in-dashboard flow.
  const handleGetQuotes = () => {
    if (user && userType === "customer") {
      navigate("/customer/quote-request");
    } else {
      navigate("/quote-request");
    }
  };

  // Redirect to login tab for returning customers
  const handleCustomerLogin = () => {
    if (user && userType === "customer") {
      navigate("/customer/dashboard");
    } else {
      navigate("/customer/auth?tab=login");
    }
  };

  const handleShopLogin = () => {
    if (user && userType === "shop") {
      navigate("/shop/dashboard");
    } else {
      navigate("/shop/auth?tab=login");
    }
  };

  // For "I'm a Tyre Shop" - go to registration
  const handleShopRegister = () => {
    if (user && userType === "shop") {
      navigate("/shop/dashboard");
    } else {
      navigate("/shop/auth?tab=register");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO page="home" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Enhanced & Prominent */}
            <Link to="/" className="flex items-center gap-2">
              <img src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} alt="TyreMatch" className="h-14 md:h-16 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                Home
              </Link>
              <Link to="/how-it-works" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="nav-how-it-works">
                How It Works
              </Link>
              <Link to="/for-shops" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="nav-for-shops">
                For Tyre Shops
              </Link>
              
              <div className="flex items-center gap-3 ml-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  className="rounded-full"
                  data-testid="theme-toggle"
                >
                  {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </Button>

                {user ? (
                  <Button
                    onClick={() => navigate(userType === "customer" ? "/customer/dashboard" : userType === "shop" ? "/shop/dashboard" : "/nedhaavu/dashboard")}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="dashboard-btn"
                  >
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleShopLogin}
                      data-testid="shop-login-btn"
                    >
                      Shop Login
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCustomerLogin}
                      data-testid="customer-login-btn"
                    >
                      Customer Login
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-4">
                <Link to="/" className="text-sm font-medium text-foreground">
                  Home
                </Link>
                <Link to="/how-it-works" className="text-sm font-medium text-muted-foreground">
                  How It Works
                </Link>
                <Link to="/for-shops" className="text-sm font-medium text-muted-foreground">
                  For Tyre Shops
                </Link>
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <Button variant="outline" onClick={handleShopLogin} className="w-full">
                    Shop Login
                  </Button>
                  <Button variant="outline" onClick={handleCustomerLogin} className="w-full">
                    Customer Login
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70 dark:from-background dark:via-background/90 dark:to-background/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6 animate-fade-in" data-testid="hero-headline">
              Get Tyre Quotes{" "}
              <span className="text-primary">From Local Shops</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 animate-fade-in stagger-1" data-testid="hero-subheadline">
              One request → private quotes from nearby local shops → compare → book. Shops can't see each other's prices.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-2">
              <Button
                size="lg"
                onClick={handleGetQuotes}
                className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 rounded-full animate-pulse-orange"
                data-testid="hero-get-quotes-btn"
              >
                <Car className="mr-2 h-5 w-5" />
                Get Tyre Quotes
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={handleShopRegister}
                className="text-lg px-8 py-6 rounded-full"
                data-testid="hero-shop-btn"
              >
                <Store className="mr-2 h-5 w-5" />
                I'm a Tyre Shop
              </Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground animate-fade-in stagger-2">
              No password needed. Already registered?{" "}
              <button
                type="button"
                onClick={handleCustomerLogin}
                className="text-primary underline hover:no-underline"
                data-testid="hero-customer-login-link"
              >
                Log in
              </button>
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-6 mt-12 animate-fade-in stagger-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-5 w-5 text-primary" />
                <span>Private Quotes</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-5 w-5 text-primary" />
                <span>Local NZ Shops</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <DollarSign className="h-5 w-5 text-primary" />
                <span>No Hidden Fees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-muted/30 tyre-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              How It <span className="text-primary">Works</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Request once → receive private quotes from nearby local shops → compare and book. The number of quotes depends on live shop coverage and your search radius.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="glass-card rounded-2xl p-8 card-hover" data-testid="step-1">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Car className="h-8 w-8 text-primary" />
              </div>
              <div className="text-5xl font-heading font-bold text-primary mb-4">01</div>
              <h3 className="font-heading text-2xl font-semibold mb-3">Submit Your Request</h3>
              <p className="text-muted-foreground">
                Enter your vehicle rego or tyre specs, choose your service type, and set your search radius. It takes less than 2 minutes.
              </p>
            </div>

            {/* Step 2 */}
            <div className="glass-card rounded-2xl p-8 card-hover" data-testid="step-2">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              <div className="text-5xl font-heading font-bold text-primary mb-4">02</div>
              <h3 className="font-heading text-2xl font-semibold mb-3">Receive Private Quotes</h3>
              <p className="text-muted-foreground">
                Compare private quotes from nearby shops — the number depends on live local coverage and your radius. Shops can't see each other's prices.
              </p>
            </div>

            {/* Step 3 */}
            <div className="glass-card rounded-2xl p-8 card-hover" data-testid="step-3">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <div className="text-5xl font-heading font-bold text-primary mb-4">03</div>
              <h3 className="font-heading text-2xl font-semibold mb-3">Choose & Book</h3>
              <p className="text-muted-foreground">
                Compare quotes, check shop ratings, and select your preferred option. The shop will arrange your fitment.
              </p>
            </div>
          </div>

          {/* Link to full how-it-works page */}
          <div className="text-center mt-10">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-1 text-primary font-semibold hover:underline"
              data-testid="home-see-full-how-it-works"
            >
              See how it works in full
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
                Why Choose <span className="text-primary">TyreMatch</span>?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We've built the most transparent tyre marketplace in New Zealand. No phone calls, no haggling, just fair quotes from trusted local shops.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4" data-testid="feature-privacy">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Eye className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl font-semibold mb-1">Complete Privacy</h4>
                    <p className="text-muted-foreground">
                      Your details stay hidden until you choose a quote. Shops compete on price and service, not sales tactics.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4" data-testid="feature-local">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl font-semibold mb-1">Local First</h4>
                    <p className="text-muted-foreground">
                      Support local NZ tyre shops. Set your radius and get quotes from businesses near you.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4" data-testid="feature-ratings">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Star className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl font-semibold mb-1">Verified Ratings</h4>
                    <p className="text-muted-foreground">
                      See Google ratings and platform reviews. Make informed decisions based on real feedback.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={MECHANIC_IMG} 
                  alt="Professional tyre fitting" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-6 max-w-[280px]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold">Average Savings</p>
                    <p className="text-2xl font-heading font-bold text-primary">$150+</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Customers save by comparing quotes from multiple shops
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Tyre Shops */}
      <section id="for-shops" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={WORKSHOP_IMG} 
                  alt="Tyre workshop" 
                  className="w-full h-auto"
                />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Store className="h-4 w-4" />
                For Tyre Shops
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
                Grow Your <span className="text-primary">Business</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Inbound quote requests from drivers in your area. You set the price — competitors can't see your quote. Respond within 24 hours (aim under 2 on weekdays) for the best driver experience.
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>Receive requests from drivers within your service area</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>Submit up to 5 quotes per request to offer variety</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>Commission on completed jobs — confirmed on approval</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span>Build your reputation with real customer reviews</span>
                </li>
              </ul>

              <Button
                size="lg"
                onClick={() => navigate("/for-shops")}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                data-testid="shops-cta-btn"
              >
                Learn more — For Tyre Shops
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
            Ready to Save on Your Next Tyres?
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-8">
            Compare private quotes from local NZ tyre shops — no phone tag, no obligation.
          </p>
          <Button
            size="lg"
            onClick={handleGetQuotes}
            className="bg-background text-foreground hover:bg-background/90 text-lg px-8 py-6 rounded-full"
            data-testid="cta-get-quotes-btn"
          >
            Get Your Free Quotes Now
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Large Logo Section - Left side */}
            <div className="lg:col-span-5 flex flex-col items-center lg:items-start">
              <Link to="/">
                <img 
                  src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} 
                  alt="TyreMatch" 
                  className="h-24 md:h-32 lg:h-40 w-auto mb-6" 
                />
              </Link>
              <p className="text-muted-foreground text-center lg:text-left max-w-sm mb-4">
                New Zealand's smartest way to compare tyre quotes and book services.
              </p>
              <p className="text-2xl font-heading font-bold text-primary mb-4">
                Get Quotes. Compare. Save.
              </p>
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} TyreMatch. All rights reserved.
              </p>
            </div>

            {/* Content Section - Right side */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div>
                <h4 className="font-heading font-semibold text-lg mb-4">Quick Links</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/how-it-works" className="text-muted-foreground hover:text-primary transition-colors">
                      How It Works
                    </Link>
                  </li>
                  <li>
                    <Link to="/tread-depth-nz" className="text-muted-foreground hover:text-primary transition-colors">
                      Tyre Tread Depth NZ
                    </Link>
                  </li>
                  <li>
                    <Link to="/for-shops" className="text-muted-foreground hover:text-primary transition-colors">
                      For Tyre Shops
                    </Link>
                  </li>
                  <li>
                    <Link to="/customer/auth" className="text-muted-foreground hover:text-primary transition-colors">
                      Customer Login
                    </Link>
                  </li>
                  <li>
                    <Link to="/shop/auth" className="text-muted-foreground hover:text-primary transition-colors">
                      Shop Login
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-heading font-semibold text-lg mb-4">Services</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li>Tyres</li>
                  <li>Wheel Alignment</li>
                  <li>Tyre Rotation</li>
                </ul>
              </div>

              <div>
                <h4 className="font-heading font-semibold text-lg mb-4">Contact</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li>
                    <a href="mailto:sales@tyrematch.co.nz" className="hover:text-primary transition-colors">
                      sales@tyrematch.co.nz
                    </a>
                  </li>
                  <li>Auckland, New Zealand</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Marketplace disclosure */}
          <div className="mt-12 pt-8 border-t border-border text-center">
            <p className="text-xs text-muted-foreground max-w-2xl mx-auto">
              TyreMatch is a marketplace connecting NZ drivers with local tyre shops. You pay the shop directly for fitment — TyreMatch is not the tyre seller or the payment processor for the fitment.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
